/**
 * Inventory Consumption List (read-only)
 * Consumption records are created via the Material Request Note "Release" action.
 * This view lists them with item details, cost center (consumer), warehouse, and
 * the actual released quantity per line.
 */

import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import inventoryApi from '../../../services/inventoryApi';

const InventoryConsumptionList = () => {
    const [consumptions, setConsumptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    const searchTimeoutRef = useRef(null);

    const [viewing, setViewing] = useState(null);

    const [view, setView] = useState(() => {
        try {
            return localStorage.getItem('invConsumptionListView') || 'card';
        } catch {
            return 'card';
        }
    });

    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 500);
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchInput]);

    useEffect(() => {
        loadConsumptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchInput, pagination.page, pagination.limit]);

    const loadConsumptions = async () => {
        try {
            setLoading(true);
            const params = { page: pagination.page, limit: pagination.limit };
            if (debouncedSearchInput) params.search = debouncedSearchInput;

            const result = await inventoryApi.getInventoryConsumptions(params);
            setConsumptions(result.data || []);
            if (result.pagination) {
                setPagination({
                    page: result.pagination.page || 1,
                    limit: result.pagination.limit || 20,
                    total: result.pagination.total || 0,
                    pages: result.pagination.pages || 0
                });
            }
            setError('');
        } catch (err) {
            console.error('Error loading inventory consumptions:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load inventory consumptions');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => setPagination(prev => ({ ...prev, page: newPage }));
    const handleLimitChange = (newLimit) => setPagination(prev => ({ page: 1, limit: newLimit, total: prev.total, pages: prev.pages }));

    const handleViewChange = (newView) => {
        setView(newView);
        try {
            localStorage.setItem('invConsumptionListView', newView);
        } catch (error) {
            console.warn('Failed to save view preference:', error);
        }
    };

    const clearFilters = () => {
        setSearchInput('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasFilters = () => !!searchInput;

    const totalReleased = (c) => (c.lineItems || []).reduce((sum, l) => sum + (l.quantityReleased || 0), 0);

    const renderCard = (c) => (
        <div key={c._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group w-full">
            <div className="p-3 flex flex-col gap-2">
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {c.consumptionNo?.slice(-2) || 'IC'}
                        </div>
                        <div className="min-w-0 flex-1">
                            <button
                                onClick={() => setViewing(c)}
                                className="text-sm font-semibold text-gray-900 hover:text-indigo-600 transition-colors truncate text-left block"
                            >
                                {c.consumptionNo}
                            </button>
                            <p className="text-xs text-gray-400">{c.consumptionDate ? new Date(c.consumptionDate).toLocaleDateString() : ''}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setViewing(c)}
                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100 flex-shrink-0"
                        title="View"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </button>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="truncate">{c.costCenterName || '—'}</span>
                    <span className="truncate text-gray-400 ml-2">{c.mrnNo || '—'}</span>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-600 truncate">
                        {c.lineItems?.length || 0} items{c.warehouseName ? ` • ${c.warehouseName}` : ''}
                    </span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border bg-emerald-100 text-emerald-700 border-emerald-300 flex-shrink-0 ml-2">
                        {totalReleased(c)} released
                    </span>
                </div>
            </div>
        </div>
    );

    const renderRow = (c) => (
        <tr key={c._id} className="hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                        {c.consumptionNo?.slice(-2) || 'IC'}
                    </div>
                    <div>
                        <button onClick={() => setViewing(c)} className="text-sm font-semibold text-gray-900 hover:text-indigo-600">
                            {c.consumptionNo}
                        </button>
                        <div className="text-xs text-gray-400">{c.consumptionDate ? new Date(c.consumptionDate).toLocaleDateString() : ''}</div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{c.mrnNo || '—'}</td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{c.costCenterName || '—'}</td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{c.warehouseName || '—'}</td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{c.lineItems?.length || 0} items</td>
            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">{totalReleased(c)}</td>
        </tr>
    );

    if (loading && consumptions.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="bg-white shadow-sm border border-gray-200">
            {error && (
                <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
            )}

            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                    <div className="flex-1">
                        <TextInput
                            variant="input"
                            type="text"
                            placeholder="Search by consumption number, MRN, cost center, items..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            icon={({ className }) => (
                                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            )}
                            className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        {hasFilters() && (
                            <button
                                onClick={clearFilters}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                title="Clear all filters"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}

                        {/* View Toggle */}
                        <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                            <button
                                onClick={() => handleViewChange('card')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                                title="Card view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                                </svg>
                                Cards
                            </button>
                            <button
                                onClick={() => handleViewChange('table')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'table' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                                title="List view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                                List
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <div className="flex items-center justify-between px-2 py-2 bg-gradient-to-r from-gray-100 to-gray-100 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-900">
                                {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
                            </p>
                            <p className="text-[10px] text-gray-500">
                                {hasFilters() ? 'Filtered results' : 'All consumption records'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 font-medium">Show:</span>
                            <CustomSelect
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                                disabled={loading}
                                options={[
                                    { value: 10, label: '10' },
                                    { value: 20, label: '20' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                size="sm"
                                variant="minimal"
                                className="min-w-[70px]"
                            />
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.page <= 1 || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="First page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                                disabled={pagination.page <= 1 || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className="hidden sm:inline">Previous</span>
                            </button>

                            <div className="hidden md:flex items-center gap-1">
                                {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                                    const pageNum = i + 1;
                                    const isCurrentPage = pageNum === pagination.page;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            disabled={loading}
                                            className={`min-w-[28px] px-2 py-1.5 rounded text-xs font-medium transition-all ${isCurrentPage ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                {pagination.pages > 7 && <span className="px-2 text-gray-400 text-xs">...</span>}
                            </div>

                            <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                                <span className="text-xs font-medium text-gray-900">{pagination.page}</span>
                                <span className="text-xs text-gray-500 mx-1">of</span>
                                <span className="text-xs font-medium text-gray-900">{pagination.pages}</span>
                            </div>

                            <button
                                onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <span className="hidden sm:inline">Next</span>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.pages)}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="Last page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="p-2">
                {consumptions.length === 0 ? (
                    <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No inventory consumption records found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {hasFilters() ? 'Try adjusting your search.' : 'Records appear here when materials are released against a request note.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {view === 'card' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {consumptions.map(renderCard)}
                            </div>
                        )}
                        {view === 'table' && (
                            <div className="overflow-x-auto">
                                <table className="min-w-max w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Consumption</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">MRN</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Cost Center</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Warehouse</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Items</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Released</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {consumptions.map(renderRow)}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* View modal */}
            {viewing && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
                    <div className="flex items-center justify-center min-h-full px-4 py-6">
                        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
                            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white">{viewing.consumptionNo}</h3>
                                <button onClick={() => setViewing(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="px-4 py-3">
                                <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-gray-600">
                                    <div><span className="font-medium text-gray-500">MRN: </span>{viewing.mrnNo || '—'}</div>
                                    <div><span className="font-medium text-gray-500">Date: </span>{viewing.consumptionDate ? new Date(viewing.consumptionDate).toLocaleDateString() : '—'}</div>
                                    <div><span className="font-medium text-gray-500">Cost Center: </span>{viewing.costCenterName || '—'}</div>
                                    <div><span className="font-medium text-gray-500">Warehouse: </span>{viewing.warehouseName || '—'}</div>
                                    {viewing.notes && (
                                        <div className="col-span-2"><span className="font-medium text-gray-500">Notes: </span>{viewing.notes}</div>
                                    )}
                                </div>
                                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                                    <table className="min-w-max w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Item</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">UoM</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Requested</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Available (at release)</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Released</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {(viewing.lineItems || []).map((line, index) => (
                                                <tr key={index}>
                                                    <td className="px-2 py-1.5">
                                                        <div className="text-xs font-medium text-gray-900">{line.itemDescription}</div>
                                                        <div className="text-xs text-gray-500 font-mono">{line.itemCode}</div>
                                                    </td>
                                                    <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.uom || '—'}</td>
                                                    <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityRequested}</td>
                                                    <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.availableStock ?? '—'}</td>
                                                    <td className="px-2 py-1.5 text-center text-xs font-medium text-gray-900">{line.quantityReleased}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryConsumptionList;
